/**
 * **The inverse of the projection.** Given a value the trainer typed into week 5, the base the rule
 * must start from so that its curve passes THROUGH that value at week 5 and carries on from there.
 *
 * This is the second of the two answers the calendar's day editor offers when a programmed value
 * changes on a running plan:
 *
 * · „tylko ten tydzień" keeps the pin. Week 5 reads 90 and week 6 reads whatever the old curve said.
 *   Nothing here is involved — the override already IS that answer.
 * · „ten tydzień i wszystkie kolejne" shifts the base. At +2,5 kg per cycle, typing 90 into week 5
 *   makes week 5 read 90 and **week 6 read 92,5** — the curve moved, it did not flatten.
 *
 * The second is only expressible as an inversion, because the base is the only thing the tree stores
 * and every later cycle is derived from it. Writing 90 onto the base instead would put 90 in week 1
 * and 100 in week 5; writing 90 into every cycle as a pin would flatten the progression the trainer
 * built.
 *
 * ── It must never lie about what it produced ─────────────────────────────────────────────────
 * `projectSnapped` snaps a machine-computed value to the axis's grid — a loadable plate on the
 * weight axis, a half point on RPE — so a base inverted out of a snapped number does not always
 * project back onto it. A compound rate is where this bites: 90 ÷ 1,05⁴ is 74,04, whose projection
 * rounds to 90,05 and not to 90.
 *
 * So the outcome carries `keepsOverride`. The base shift stands either way — later cycles continue
 * from the new curve, which is what the trainer asked for — and where the round trip does not land
 * exactly, the pin at THIS cycle survives so the week they are looking at reads exactly what they
 * typed. Where it does land, the pin is dropped: a redundant pin is clutter that would outlive every
 * later change to the rule and silently hold one cell back.
 *
 * ── A back-off descent inverts to itself, and that is not a special case being dodged ────────
 * A descent has no rule of its own — `buildMatrix` replicates its base percent across every cycle,
 * because what moves the descent is the top set's progression. Its inversion is therefore the
 * identity, and it round-trips exactly.
 *
 * ── What this does to the cycles BEFORE the one being edited ─────────────────────────────────
 * They move too, and the brief accepts it explicitly: a past cycle's workout is a frozen snapshot
 * carrying its own prescription (`training-plan` R3), so the template row behind it is not what the
 * client trained from. Nothing the athlete has done changes.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { ExerciseDraft } from './planDraft.types';
import { axisForExercise, normaliseTypedValue, type AxisValueField } from './progressionAxis';
import {
  findOverride,
  overrideValueOn,
  projectSnapped,
  ruleFromExercise,
  type ProjectionRule,
} from './progressionMatrix';
import { derivesLoadInSession } from './setShape';

/** Round to 2 decimals to shed the float noise a compound division leaves behind. */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The base a rule must start from for its projection to reach `target` after `cycleOffset` cycles.
 *
 * The exact inverse of `projectValue`, branch for branch:
 * · `flat` moves nothing — the base is what the cell holds.
 * · `additive` adds a constant per cycle — subtract `value × offset`.
 * · `compound` multiplies by `(1 + value/100)` per cycle — divide by that power.
 *
 * Offset 0 or less is the base column itself and comes back untouched. A compound rate of −100 %
 * collapses the factor to zero and has no inverse; `target` is returned rather than an infinity,
 * which is the same answer the projection gives (a floored zero cannot be un-floored either).
 */
export function invertProjection(
  target: number,
  cycleOffset: number,
  rule: ProjectionRule,
): number {
  if (cycleOffset <= 0) return target;
  if (rule.kind === 'flat') return target;
  if (rule.kind === 'additive') return round2(target - rule.value * cycleOffset);
  const factor = Math.pow(1 + rule.value / 100, cycleOffset);
  return factor === 0 ? target : round2(target / factor);
}

/** What „ten tydzień i wszystkie kolejne" writes, and whether this cycle still needs its pin. */
export interface CycleRebase {
  /** The set field the new base is written to — the progression axis, or a descent's percent. */
  readonly field: AxisValueField;
  /** The value to write onto the authored set. */
  readonly base: number;
  /**
   * The projection does not land back exactly on what the trainer typed, so the pin at this cycle
   * has to stay or the week they are looking at would change under them.
   */
  readonly keepsOverride: boolean;
}

/**
 * Turn the pin sitting on one cell into a base shift.
 *
 * `null` when there is nothing to shift: no such set, no pin on that cell, or a pin carrying no
 * value in the field the exercise progresses on. Each of those is a caller asking about a cell it
 * never wrote, and answering with a made-up base would rewrite a prescription nobody touched.
 *
 * @param baseCycle The exercise's own first active cycle — `CycleWindow.startCycle`. Handed in
 *   rather than re-derived, exactly as `MatrixCellAddress` does and for the same reason: the origin
 *   rule lives in `cycleWindow.ts` and a second walk here would be a second answer to it.
 */
export function rebaseToCycle(
  exercise: ExerciseDraft,
  setIndex: number,
  cycleIndex: number,
  baseCycle: number,
): CycleRebase | null {
  const set = exercise.sets[setIndex - 1];
  if (set === undefined) return null;

  const override = findOverride(exercise.progressionOverrides, setIndex, cycleIndex);
  if (override === undefined) return null;

  // A descent's whole prescription is its percent, and it inherits no rule — so the shift is the
  // value itself and it round-trips by construction.
  if (derivesLoadInSession(set)) {
    const pinned = override.prescribedPercent;
    if (pinned === null) return null;
    return { field: 'prescribedPercent', base: pinned, keepsOverride: false };
  }

  const axis = axisForExercise(exercise);
  const pinned = overrideValueOn(override, axis);
  if (pinned === null) return null;

  const rule = ruleFromExercise(exercise, axis);
  const offset = cycleIndex - baseCycle;
  const base = normaliseTypedValue(Math.max(0, invertProjection(pinned, offset, rule)), axis);
  const round = projectSnapped(base, offset, rule, axis);

  return {
    field: axis.setField,
    base,
    keepsOverride: round === null || Math.abs(round - pinned) > 1e-6,
  };
}

